import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getObstacleIcon, getArpIcon, getShieldComboIcon } from '../../utils/markers';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';
import MapMeasureTool from '../../components/MapMeasureTool';
import Cesium3DPreviewModal from '../../components/Cesium3DPreviewModal';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { useShield, exportSurfacePenetratingCsv } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';
import { assessProjectDepartures, saveSidScreenshot, deleteSidScreenshot } from '../../api/client';

// Role-based styling for SID departure procedure features (DEP_PROCEDURE_LEAFLET_INSTRUCTIONS.md)
// Defaults only — the derived `sidRoleMeta` built inside the component (via
// getStyle('sid_ois.*')) is what's actually used, so Settings customization applies.
const DEFAULT_SID_ROLE_META = {
    track:     { weight: 3,   label: 'Track' },
    primary:   { weight: 2.5, label: 'Primary Area' },
    secondary: { weight: 2,   label: 'Secondary Area' },
    waypoint:  { weight: 2,   label: 'Waypoint' },
};
function sidResolveRole(feature) {
    return feature.type === 'point' ? 'waypoint' : feature.role;
}
function sidAddArrowhead(map, latlngs, color, opacity) {
    const n = latlngs.length;
    if (n < 2) return null;
    const p1 = latlngs[n - 2], p2 = latlngs[n - 1];
    const ang = Math.atan2(p2[1] - p1[1], p2[0] - p1[0]) * 180 / Math.PI;
    const marker = L.marker(p2, {
        icon: L.divIcon({
            className: '',
            html: `<div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-bottom:12px solid ${color};transform:rotate(${90 + ang}deg);transform-origin:center;opacity:${opacity};"></div>`,
            iconSize: [12, 12], iconAnchor: [6, 6],
        }),
        interactive: false,
    });
    marker.addTo(map);
    return marker;
}
// ICAO fly-by/fly-over waypoint symbology (DEP_PROCEDURE_LEAFLET_INSTRUCTIONS.md
// / ARRIVAL_CLEARANCE_ENGINE.md convention) — never plain circles.
function waypointIcon(turnType) {
    const url = turnType === 'flyover' ? '/symbols/flyover.svg' : '/symbols/flyby.svg';
    return L.icon({ iconUrl: url, iconSize: [22, 22], iconAnchor: [11, 11] });
}
// Permanent text label flowing to the right of a waypoint symbol — a plain
// divIcon (not a Leaflet tooltip) so it never hides on mouseout and never
// intercepts clicks meant for the symbol underneath.
function waypointLabel(map, latlng, text) {
    const marker = L.marker(latlng, {
        icon: L.divIcon({
            className: '',
            html: `<div style="white-space:nowrap; padding-left:8px; font-size:9px; font-weight:700; color:#fff; text-shadow:1px 1px 2px #000,-1px -1px 2px #000,1px -1px 2px #000,-1px 1px 2px #000;">${text}</div>`,
            iconSize: [120, 14],
            iconAnchor: [0, 7],
        }),
        interactive: false,
    });
    marker.addTo(map);
    return marker;
}

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const exportToCsv = (headers, rows, filename) => {
    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(val => {
            const cleanVal = String(val ?? '').replace(/—/g, '-');
            return `"${cleanVal.replace(/"/g, '""')}"`;
        }).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

export default function DepartureOisPage({ project, arps, obstacles, loading, oisState, setOisState, showObstacles = true, showObsLabels, showArpLabels, onObstacleContextMenu, onMapReady, onAerodromeContextMenu }) {
    const { getStyle } = useSurfaceStyles();
    const sidRoleMeta = useMemo(() => {
        const out = {};
        Object.entries(DEFAULT_SID_ROLE_META).forEach(([role, base]) => {
            const s = getStyle(`sid_ois.${role}`);
            out[role] = { ...base, color: s.color, opacity: 1, fillColor: s.color, fillOpacity: s.opacity };
        });
        return out;
    }, [getStyle]);
    const oisBoundaryStyle = getStyle('sid_ois.ois_boundary');
    const mapRef = useRef(null);
    const mapInst = useRef(null);
    // Step 2 reference set — this project's own obstacles that actually
    // penetrate the SID/Departure OIS, not just its tallest point overall.
    const depResultsForShield = oisState?.depResults || {};
    const penetratingObstacleIds = useMemo(() => {
        const ids = [];
        Object.values(depResultsForShield).forEach(entry => {
            (entry?.data?.results || []).forEach(r => { if (r.penetrates) ids.push(r.obstacle_id); });
        });
        return ids;
    }, [depResultsForShield]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv, shieldLayerRef } = useShield(mapInst, obstacles, penetratingObstacleIds);
    const tileLyr = useRef(null);
    const mkrLyrs = useRef([]);
    const surfLyrs = useRef([]);

    const [mapMode, setMapMode] = useState('street');
    const [selObstId, setSelId] = useState('');
    const [mapState, setMapState] = useState(null);
    const [exportingAixm, setExportingAixm] = useState(false);
    const lastFittedObsId = useRef(null);
    const [resultsCollapsed, setResultsCollapsed] = useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(300, { min: 120, max: 700 });
    const [resultsPoppedOut, setResultsPoppedOut] = useState(false);

    useEffect(() => {
        if (mapInst.current) {
            setTimeout(() => { mapInst.current?.invalidateSize(); }, 200);
        }
    }, [resultsCollapsed, resultsPoppedOut]);

    // Bind state externally
    const visible = oisState?.visible || {};
    const setVisible = useCallback((val) => setOisState(prev => ({ ...prev, visible: typeof val === 'function' ? val(prev.visible || {}) : val })), [setOisState]);

    const rwyVisible = oisState?.rwyVisible || {};
    const setRwyVisible = useCallback((val) => setOisState(prev => ({ ...prev, rwyVisible: typeof val === 'function' ? val(prev.rwyVisible || {}) : val })), [setOisState]);

    const depResults = oisState?.depResults || {};
    const setDepResults = useCallback((val) => setOisState(prev => ({ ...prev, depResults: typeof val === 'function' ? val(prev.depResults || {}) : val })), [setOisState]);

    // ── SID / Departure OIS shielding — real, not flat ───────────────────────
    // Same reasoning as OLS/VSS/Missed Approach: the OIS is a 2.5% sloped
    // surface from the DER, so "shields" has to mean "already penetrates
    // THIS surface, at its own position, by at least as much as the
    // project's own worst obstacle does" — not "is taller". Reuses
    // /departure-ois/assess, the exact engine already used for real project
    // obstacles (see handleLoadOis above), for candidates too. Note the sign
    // convention here: the backend doesn't return a margin directly, only
    // surface_elev_m / total_obs_elev_m / penetrates — margin is derived as
    // surface_elev_m - total_obs_elev_m (negative = penetrates, matching OLS).
    const [sidShieldPens, setSidShieldPens] = useState({}); // icao -> candidate result rows
    const [sidShieldLoading, setSidShieldLoading] = useState(false);

    const sidImpacts = useMemo(() => {
        const out = {}; // `${icao}_${runway}` -> worst (most negative) margin
        Object.entries(depResults).forEach(([icao, entry]) => {
            (entry?.data?.results || []).forEach(r => {
                if (!r.penetrates || r.surface_elev_m == null || r.total_obs_elev_m == null) return;
                const margin = r.surface_elev_m - r.total_obs_elev_m;
                const key = `${icao}_${r.runway}`;
                if (!(key in out) || margin < out[key]) out[key] = margin;
            });
        });
        return out;
    }, [depResults]);
    const hasSidImpact = Object.keys(sidImpacts).length > 0;

    useEffect(() => {
        if (!shieldOn || !shieldData || !hasSidImpact) { setSidShieldPens({}); return; }
        const icaos = [...new Set(Object.keys(sidImpacts).map(k => k.split('_')[0]))];
        if (!icaos.length) return;

        const candidates = [
            ...(shieldData.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
            ...(shieldData.sof || []).map(r => ({ ...r, kind: 'SOF' })),
        ].filter(r => r.lat != null && r.lon != null);
        if (!candidates.length) { setSidShieldPens({}); return; }

        const pseudoObstacles = candidates.map(r => ({
            id: `shield_${r.kind}_${r.ogc_fid}`,
            name: r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type),
            latitude: r.lat, longitude: r.lon,
            elevation_m: parseFloat(r.elev_m) || 0,
            v_tolerance: parseFloat(r.v_tolerance_m) || 0,
            h_tolerance: 0,
        }));

        let cancelled = false;
        (async () => {
            setSidShieldLoading(true);
            const perIcao = {};
            for (const icao of icaos) {
                try {
                    const res = await fetch(`${API_BASE}/departure-ois/assess`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ aerodrome: icao, obstacles: pseudoObstacles }),
                    });
                    const data = await res.json();
                    perIcao[icao] = (data.results || []).map(r => ({ ...r, margin: r.surface_elev_m - r.total_obs_elev_m }));
                } catch (e) {
                    console.error(`SID OIS shield check failed for ${icao}`, e);
                }
            }
            if (!cancelled) setSidShieldPens(perIcao);
            setSidShieldLoading(false);
        })();
        return () => { cancelled = true; };
    }, [shieldOn, shieldData, hasSidImpact, sidImpacts]);

    // Candidate pseudo-id -> { icao, runway, margin, projectMargin }
    const sidShieldVerdicts = useMemo(() => {
        const out = new Map();
        Object.entries(sidShieldPens).forEach(([icao, rows]) => {
            rows.forEach(r => {
                if (r.margin == null) return;
                const key = `${icao}_${r.runway}`;
                const projectMargin = sidImpacts[key];
                if (projectMargin == null) return;
                if (r.margin <= projectMargin) {
                    out.set(r.obstacle_id, { icao, runway: r.runway, margin: r.margin, projectMargin });
                }
            });
        });
        return out;
    }, [sidShieldPens, sidImpacts]);

    // Candidate pseudo-id -> true if it falls under the OIS surface polygon
    // for at least one runway (the backend only ever returns a row for a
    // candidate/runway pair when their geometry intersects — see
    // departure_ois.py's `if not obs_geom.intersects(poly): continue` — so
    // "no row anywhere" means genuinely outside every OIS surface tested).
    const sidShieldInsideSurface = useMemo(() => {
        const out = new Map();
        Object.values(sidShieldPens).forEach(rows => {
            rows.forEach(r => out.set(r.obstacle_id, true));
        });
        return out;
    }, [sidShieldPens]);

    // Candidate pseudo-id -> [{ runway, margin }, ...] for every OIS runway
    // it genuinely penetrates at its own position (margin < 0), regardless
    // of raw elevation or of beating the project's worst margin — see
    // olsShieldRealPenetration in OLSPage.jsx.
    const sidShieldRealPenetration = useMemo(() => {
        const out = new Map();
        Object.values(sidShieldPens).forEach(rows => {
            rows.forEach(r => {
                if (r.margin == null || r.margin >= 0) return;
                const list = out.get(r.obstacle_id) || [];
                list.push({ runway: r.runway, margin: r.margin });
                out.set(r.obstacle_id, list);
            });
        });
        return out;
    }, [sidShieldPens]);

    // Enrich each shield marker's own popup (from useShield) with the real
    // SID OIS verdict, same pattern as OLSPage.jsx. Also recolours the
    // marker yellow for a genuine real-surface penetration that wasn't
    // already red (raw-elevation shield).
    const comboShieldLayerRef = useRef(null); // half-red/half-yellow halo: red (raw-elevation) AND genuinely penetrates a real surface
    useEffect(() => {
        const group = shieldLayerRef.current;
        if (comboShieldLayerRef.current) { mapInst.current?.removeLayer(comboShieldLayerRef.current); comboShieldLayerRef.current = null; }
        if (!group || !hasSidImpact) return;
        const comboGroup = L.featureGroup();
        group.eachLayer(layer => {
            if (!layer._shieldKey || !layer._basePopupHtml) return;
            const v = sidShieldVerdicts.get(layer._shieldKey);
            const insideAnySurface = sidShieldInsideSurface.get(layer._shieldKey) || false;
            const realPenList = sidShieldRealPenetration.get(layer._shieldKey) || [];
            const realPenetration = realPenList.length > 0;
            const realPenNames = [...new Set(realPenList.map(v2 => `SID / Departure OIS RWY ${v2.runway}`))];
            const outsideLine = `<br/><span style="color:#8a94a6">Outside surface?: ${insideAnySurface ? 'No' : 'Yes'}</span>`;
            const penetratesLine = realPenetration
                ? `<br/><span style="color:#2D9CDB">Penetrates real surface (regardless of height)?: Yes — ${realPenNames.join(', ')}</span>`
                : `<br/><span style="color:#8a94a6">Penetrates real surface (regardless of height)?: No</span>`;
            const surfaceLine = v
                ? `<br/><b style="color:#FFB020">SID / Departure OIS: RWY ${v.runway}</b> ` +
                  `(this obstacle's margin ${v.margin.toFixed(1)} m vs project's worst ${v.projectMargin.toFixed(1)} m)`
                : `<br/><span style="color:#8a94a6">SID / Departure OIS: No</span>`;
            layer.setPopupContent(layer._basePopupHtml + outsideLine + penetratesLine + surfaceLine);
            if (realPenetration && !layer._shieldsRaw) {
                layer.setStyle({ color: '#FFE14D', fillColor: '#FFE14D', weight: 2, radius: 7, fillOpacity: 0.85 });
            } else if (realPenetration && layer._shieldsRaw) {
                const halo = L.marker(layer.getLatLng(), { icon: getShieldComboIcon(L), interactive: false });
                comboGroup.addLayer(halo);
            }
        });
        if (comboGroup.getLayers().length > 0) {
            comboShieldLayerRef.current = comboGroup;
            comboGroup.addTo(mapInst.current);
        }
    }, [shieldLayerRef, sidShieldVerdicts, sidShieldInsideSurface, sidShieldRealPenetration, hasSidImpact, shieldData, shieldOn]);

    // Distinct marker layer for every candidate confirmed to truly shield the OIS.
    const sidShieldLayerRef = useRef(null);
    useEffect(() => {
        if (!mapInst.current) return;
        if (sidShieldLayerRef.current) { mapInst.current.removeLayer(sidShieldLayerRef.current); sidShieldLayerRef.current = null; }
        if (!shieldOn || sidShieldVerdicts.size === 0) return;

        const group = L.featureGroup();
        const byId = new Map();
        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
        });

        sidShieldVerdicts.forEach((v, obsId) => {
            const r = byId.get(obsId);
            if (!r || r.lat == null || r.lon == null) return;
            const marker = L.circleMarker([r.lat, r.lon], {
                radius: 10, color: '#FFB020', weight: 3, fillColor: '#FFB020', fillOpacity: 0.35,
            });
            const label = r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type);
            marker.bindPopup(
                `<b>🛫 TRUE SID OIS SHIELD</b><br/>${r.kind}: ${label}<br/>ID: ${r.caa_id || '—'} (row ${r.ogc_fid})<br/>` +
                `Aerodrome: ${v.icao} · RWY ${v.runway}<br/>` +
                `This obstacle's own OIS margin: <b>${v.margin.toFixed(1)} m</b> (penetrates)<br/>` +
                `Project's worst-case margin there: <b>${v.projectMargin.toFixed(1)} m</b><br/>` +
                `<span style="color:#FFB020">Already penetrates as much or more — the project is not a new impact on the OIS.</span>`
            );
            group.addLayer(marker);
        });

        if (group.getLayers().length > 0) {
            sidShieldLayerRef.current = group;
            group.addTo(mapInst.current);
        }
    }, [shieldOn, sidShieldVerdicts, shieldData]);

    const capturedPics = oisState?.capturedPics || {};
    const setCapturedPics = useCallback((val) => setOisState(prev => ({ ...prev, capturedPics: typeof val === 'function' ? val(prev.capturedPics || {}) : val })), [setOisState]);

    // ── SID Departure Procedures (PANS-OPS obstacle clearance) ────────────────
    const [resultsView, setResultsView] = useState('ois'); // 'ois' | 'sid'
    const [sidResult, setSidResult] = useState(null);
    const [sidLoading, setSidLoading] = useState(false);
    const [sidError, setSidError] = useState('');
    const [activeSidProcId, setActiveSidProcId] = useState(null);
    const [sidVisible, setSidVisible] = useState({});
    const sidLyrs = useRef([]);

    const capturedSidPics = oisState?.capturedSidPics || {};
    const setCapturedSidPics = useCallback((val) => setOisState(prev => ({ ...prev, capturedSidPics: typeof val === 'function' ? val(prev.capturedSidPics || {}) : val })), [setOisState]);

    useEffect(() => {
        if (!project?.id) return;
        setSidLoading(true);
        setSidError('');
        assessProjectDepartures(project.id)
            .then(({ data }) => {
                setSidResult(data);
                const procs = data?.procedures || [];
                setSidVisible(Object.fromEntries(procs.map(p => [p.id, true])));
                if (procs.length > 0) setActiveSidProcId(procs[0].id);
            })
            .catch(err => {
                console.error('Failed to load SID departure assessment', err);
                setSidError(err.response?.data?.detail || err.message);
            })
            .finally(() => setSidLoading(false));
    }, [project?.id]);

    const sidProcedures = sidResult?.procedures || [];
    const activeSidProc = sidProcedures.find(p => p.id === activeSidProcId) || null;
    const allSidVisible = sidProcedures.length > 0 && sidProcedures.every(p => sidVisible[p.id] !== false);
    const someSidVisible = sidProcedures.some(p => sidVisible[p.id] !== false) && !allSidVisible;
    const toggleAllSid = (checked) => {
        setSidVisible(Object.fromEntries(sidProcedures.map(p => [p.id, checked])));
    };

    // Hardcoded whitelist of aerodromes that physically possess OIS logic
    const DEP_OIS_WHITELIST = ["FABL", "FACT", "FAPE", "FAGG", "FAKM", "FAEL", "FALE", "FAKN", "FALA", "FAOR"];

    const obsWithCoords = obstacles.filter(o => o.latitude && o.longitude);
    const selectedObs = obsWithCoords.find(o => o.id === selObstId) || obsWithCoords[0];

    // Restrict exactly to the whitelist AND within 15km of the assessment center
    const validArps = React.useMemo(() => arps.filter(a => 
        DEP_OIS_WHITELIST.includes(a.icao?.toUpperCase()) && 
        (a.distance_km == null || a.distance_km <= 15.0)
    ), [arps]);

    useEffect(() => {
        if (!validArps.length) return;
        setVisible(prev => {
            if (Object.keys(prev || {}).length > 0) return prev;
            const vis = {};
            validArps.forEach(a => { vis[a.icao] = true; });
            return vis;
        });
    }, [validArps, setVisible]);

    useEffect(() => {
        if (mapInst.current || !mapRef.current) return;
        mapInst.current = L.map(mapRef.current, { center: [-28.5, 25.0], zoom: 6, zoomDelta: 0.7, zoomSnap: 0.7 });
        const l = TILE_LAYERS[mapMode];
        tileLyr.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        setTimeout(() => mapInst.current?.invalidateSize(), 0);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);
    }, []);

    useEffect(() => {
        if (!mapInst.current) return;
        if (tileLyr.current) mapInst.current.removeLayer(tileLyr.current);
        const l = TILE_LAYERS[mapMode];
        tileLyr.current = L.tileLayer(l.url, { attribution: l.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    // Handle initial drawing
    useEffect(() => {
        if (!mapInst.current || loading) return;
        const map = mapInst.current;
        
        mkrLyrs.current.forEach(layer => map.removeLayer(layer));
        surfLyrs.current.forEach(layer => map.removeLayer(layer));
        mkrLyrs.current = [];
        surfLyrs.current = [];

        const bounds = [];
        let impactedObstacles = new Set();
        let isAssessed = false;

        validArps.forEach(arp => {
            if (depResults[arp.icao] && !depResults[arp.icao].loading) isAssessed = true;
            if (visible[arp.icao] === false) return;
            const resData = depResults[arp.icao]?.data;

            // Draw ARP
            if (arp.lat && arp.lon) {
                const pt = [arp.lat, arp.lon];
                bounds.push(pt);
                const m = L.marker(pt, { icon: getArpIcon(L, arp, showArpLabels) })
                    .bindPopup(`<b>✈ ${arp.icao}</b><br/>${arp.name}`);
                if (onAerodromeContextMenu) {
                    m.on('contextmenu', e => {
                        e.originalEvent.preventDefault();
                        onAerodromeContextMenu(arp, e.originalEvent.clientX, e.originalEvent.clientY);
                    });
                }
                m.addTo(map);
                mkrLyrs.current.push(m);
            }

            if (!resData) return;

            // Draw runway OIS polygons
            if (resData.polygons && resData.polygons.features) {
                resData.polygons.features.forEach(feat => {
                    const rwy = feat.properties.runway;
                    const rwyKey = `${arp.icao}_${rwy}`;
                    
                    if (rwyVisible[rwyKey] === false) return;

                    try {
                        const style = { color: oisBoundaryStyle.color, weight: 1.5, fillColor: oisBoundaryStyle.color, fillOpacity: oisBoundaryStyle.opacity };
                        const layer = L.geoJSON(feat, { style }).bindTooltip(`<b>${arp.icao} RWY ${rwy}</b><br/>Departure OIS`);
                        layer.addTo(map);
                        surfLyrs.current.push(layer);
                        try {
                            const b = layer.getBounds();
                            if (b.isValid()) bounds.push(b.getCenter());
                        } catch (e) {}
                    } catch (e) {}
                });
            }

            // Flag penetrated obstacles
            if (resData.results) {
                resData.results.forEach(r => {
                    const rwyKey = `${arp.icao}_${r.runway}`;
                    if (rwyVisible[rwyKey] === false) return;
                    if (r.penetrates) impactedObstacles.add(r.obstacle_id);
                });
            }
        });

        // Draw Obstacles
        getObstaclesToRender(obsWithCoords, project?.id, showObstacles).forEach(o => {
            const ll = [o.latitude, o.longitude]; 
            bounds.push(ll);
            const isSel = o.id === selectedObs?.id;
            const isPenetrating = impactedObstacles.has(o.id);

            let maxPen = -Infinity;
            let foundAny = false;
            let surfaceStr = '—';

            validArps.forEach(arp => {
                if (visible[arp.icao] === false) return;
                const resData = depResults[arp.icao]?.data;
                if (resData && resData.results) {
                    const obsRows = resData.results.filter(r => r.obstacle_id === o.id);
                    obsRows.forEach(r => {
                        const rwyKey = `${arp.icao}_${r.runway}`;
                        if (rwyVisible[rwyKey] === false) return;

                        foundAny = true;
                        const pen = r.total_obs_elev_m - r.surface_elev_m;
                        if (pen > maxPen) {
                            maxPen = pen;
                            surfaceStr = `Departure OIS RWY ${r.runway}`;
                        }
                    });
                }
            });

            let penetrationStr = '—';
            if (foundAny) {
                if (maxPen > 0) {
                    penetrationStr = `${maxPen.toFixed(2)} m`;
                } else {
                    penetrationStr = `${maxPen.toFixed(2)} m (Clear)`;
                }
            }

            const obsIconUrl = isPenetrating ? '/symbols/obstacle_red.svg' : '/symbols/obstacle_green.svg';
            const sz = isSel ? 80 : 65; const h = Math.round(sz / 2);
            const icon = getObstacleIcon(L, o, isAssessed ? (isPenetrating ? 'penetrated' : 'clear') : undefined, showObsLabels);
            const m = L.marker(ll, { icon, zIndexOffset: isSel ? 100 : 0 })
                .bindPopup(`<b>${o.name}</b><br/>ID: ${o.id}<br/>ELEVATION : ${o.elevation_m ?? '—'} m<br/>SURFACE: ${surfaceStr}<br/>PENETRATION: ${penetrationStr}`)
                .on('click', () => setSelId(o.id));
            if (onObstacleContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(map);
            mkrLyrs.current.push(m);
        });
        addHullPolygonsToMap(map, obsWithCoords, project?.id, mkrLyrs);

        if (bounds.length > 0) {
            try {
                let finalBounds = L.latLngBounds([]);
                bounds.forEach(b => {
                    if (b instanceof L.LatLngBounds) finalBounds.extend(b);
                    else finalBounds.extend(L.latLng(b));
                });
                if (finalBounds.isValid()) {
                    if (lastFittedObsId.current !== selectedObs?.id) {
                        map.fitBounds(finalBounds, { padding: [40, 40], maxZoom: 10 });
                        lastFittedObsId.current = selectedObs?.id;
                    }
                }
            } catch (e) { console.warn("Bounds error:", e); }
        }
    }, [validArps, obstacles, loading, depResults, visible, rwyVisible, showObstacles, showObsLabels, showArpLabels, selectedObs, onObstacleContextMenu, onAerodromeContextMenu, oisBoundaryStyle]);

    // Draw SID departure procedure geometry (tracks / primary+secondary areas / waypoints)
    useEffect(() => {
        if (!mapInst.current) return;
        const map = mapInst.current;
        sidLyrs.current.forEach(layer => map.removeLayer(layer));
        sidLyrs.current = [];

        if (resultsView !== 'sid') return;

        const bounds = [];
        sidProcedures.forEach(proc => {
            if (sidVisible[proc.id] === false) return;
            const isActive = proc.id === activeSidProcId;

            (proc.features || []).forEach(f => {
                const role = sidResolveRole(f);
                const meta = sidRoleMeta[role];
                if (!meta) return;
                const baseOpacity = isActive ? 1 : 0.35;
                const cleanName = (f.name || '').replace(/^WP\s+/i, '');

                if (f.type === 'point') {
                    const ll = [f.coords[0], f.coords[1]];
                    bounds.push(ll);
                    const m = L.marker(ll, { icon: waypointIcon(f.turn_type), opacity: baseOpacity });
                    m.addTo(map);
                    sidLyrs.current.push(m);
                    if (isActive) {
                        sidLyrs.current.push(waypointLabel(map, ll, cleanName || 'Waypoint'));
                    }
                } else {
                    const latlngs = f.coords.map(c => [c[0], c[1]]);
                    latlngs.forEach(ll => bounds.push(ll));
                    if (f.type === 'line') {
                        const line = L.polyline(latlngs, {
                            color: meta.color, weight: meta.weight, opacity: meta.opacity * baseOpacity,
                        }).bindTooltip(`${cleanName || meta.label}`, { sticky: true });
                        line.addTo(map);
                        sidLyrs.current.push(line);
                        if (isActive) {
                            const arrow = sidAddArrowhead(map, latlngs, meta.color, meta.opacity);
                            if (arrow) sidLyrs.current.push(arrow);
                        }
                    } else if (f.type === 'polygon') {
                        const poly = L.polygon(latlngs, {
                            color: meta.color, weight: meta.weight, opacity: meta.opacity * baseOpacity,
                            fillColor: meta.fillColor,
                            fillOpacity: meta.fillOpacity * baseOpacity,
                        }).bindTooltip(`${proc.procedure_name}<br/>${cleanName || meta.label}`, { sticky: true });
                        poly.addTo(map);
                        sidLyrs.current.push(poly);
                    }
                }
            });
        });

        if (bounds.length > 0 && activeSidProc) {
            try {
                const b = L.latLngBounds(bounds);
                if (b.isValid()) map.fitBounds(b, { padding: [40, 40], maxZoom: 11 });
            } catch (e) {}
        }
    }, [resultsView, sidProcedures, sidVisible, activeSidProcId, sidRoleMeta]);

    // Manual Fetch Launcher
    const handleLoadOis = (icao) => {
        if (!obsWithCoords.length) return;
        setDepResults(prev => ({ ...prev, [icao]: { loading: true } }));
        
        fetch(`${API_BASE}/departure-ois/assess`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ aerodrome: icao, obstacles: obsWithCoords })
        })
        .then(res => res.json())
        .then(data => {
            setDepResults(prev => ({ ...prev, [icao]: { loading: false, data, error: data.error } }));
            // Auto-enable all runways initially 
            if (data && data.polygons && data.polygons.features) {
                setRwyVisible(rv => {
                    const nextRv = { ...rv };
                    data.polygons.features.forEach(f => {
                        const key = `${icao}_${f.properties.runway}`;
                        if (nextRv[key] === undefined) nextRv[key] = true;
                    });
                    return nextRv;
                });
            }
        })
        .catch(err => {
            setDepResults(prev => ({ ...prev, [icao]: { loading: false, error: err.message } }));
        });
    };

    const captureMapDataUrl = async () => {
        const container = mapInst.current?.getContainer();
        if (!container) return null;

        try {
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);

            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                const tx = Math.floor(tRect.left - containerRect.left);
                const ty = Math.floor(tRect.top - containerRect.top);
                const tw = Math.ceil(tRect.width) + 1;
                const th = Math.ceil(tRect.height) + 1;
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => { ctx.drawImage(img, tx, ty, tw, th); URL.revokeObjectURL(url); resolve(); };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch { }
            }));

            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const sx = Math.round(svgRect.left - containerRect.left);
                const sy = Math.round(svgRect.top - containerRect.top);
                const clone = svgEl.cloneNode(true);
                clone.style.transform = '';
                clone.style.left = '0';
                clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { 
                        ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height)); 
                        URL.revokeObjectURL(url); resolve(); 
                    };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }

            const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
            for (const marker of markerEls) {
                const mRect = marker.getBoundingClientRect();
                const x = Math.round(mRect.left - containerRect.left);
                const y = Math.round(mRect.top - containerRect.top);
                const img = marker.querySelector('img');
                if (img && img.complete && img.naturalWidth) {
                    try { ctx.drawImage(img, x, y, Math.round(mRect.width), Math.round(mRect.height)); } catch {}
                }
            }
            
            const divIconEls = [...container.querySelectorAll('.leaflet-marker-icon div')];
            for (const el of divIconEls) {
                if (el.querySelector('div')) continue;
                const eRect = el.getBoundingClientRect();
                const x = Math.round(eRect.left - containerRect.left);
                const y = Math.round(eRect.top - containerRect.top);
                const text = el.textContent?.trim();
                if (text) {
                    ctx.save();
                    ctx.font = 'bold 11px sans-serif';
                    ctx.fillStyle = el.style.color || '#ff9800';
                    ctx.strokeStyle = 'rgba(0,0,0,0.7)';
                    ctx.lineWidth = 3;
                    ctx.strokeText(text, x, y + 10);
                    ctx.fillText(text, x, y + 10);
                    ctx.restore();
                }
            }

            return canvas.toDataURL('image/jpeg', 0.88);
        } catch (err) {
            console.error('Failed to capture screenshot:', err);
            return null;
        }
    };

    const handleScreenshot = async (icao) => {
        const dataUrl = await captureMapDataUrl();
        if (!dataUrl) return;
        try {
            const req = await fetch(`${API_BASE}/departure-ois/upload-screenshot?icao=${icao}&project_id=${project?.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image: dataUrl })
            });
            if (req.ok) {
                setCapturedPics(prev => ({ ...prev, [icao]: true }));
            }
        } catch (err) {
            console.error('Failed to upload screenshot:', err);
        }
    };

    const handleClearScreenshot = async (icao) => {
        try {
            const req = await fetch(`${API_BASE}/departure-ois/screenshot?icao=${icao}&project_id=${project?.id}`, { method: 'DELETE' });
            if (req.ok) {
                setCapturedPics(prev => {
                    const next = { ...prev };
                    delete next[icao];
                    return next;
                });
            }
        } catch (err) { console.error(err); }
    };

    const handleSidScreenshot = async (procId) => {
        const dataUrl = await captureMapDataUrl();
        if (!dataUrl) return;
        try {
            await saveSidScreenshot(procId, project?.id, dataUrl);
            setCapturedSidPics(prev => ({ ...prev, [procId]: true }));
        } catch (err) { console.error('Failed to save SID screenshot:', err); }
    };

    const handleClearSidScreenshot = async (procId) => {
        try {
            await deleteSidScreenshot(procId, project?.id);
            setCapturedSidPics(prev => {
                const next = { ...prev };
                delete next[procId];
                return next;
            });
        } catch (err) { console.error(err); }
    };

    const [preview3DIcao, setPreview3DIcao] = useState(null);

    const handleDownloadKmz = React.useCallback(async (icao, mode) => {
        try {
            const url = `${API_BASE}/departure-ois/${icao}/kml?mode=${mode}${project?.id ? `&project_id=${project.id}` : ''}`;
            
            const response = await fetch(url);
            if (!response.ok) {
                const text = await response.text();
                throw new Error(`Failed to download KMZ: ${text}`);
            }
            
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${icao}_SID_OIS_${mode.toUpperCase()}.kmz`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('KMZ Export error:', err);
            alert("Failed to export KMZ: " + err.message);
        }
    }, []);

    // Format Table Rows
    const tableRows = [];
    validArps.forEach(arp => {
        if (!visible[arp.icao]) return;
        const resData = depResults[arp.icao]?.data;
        if (!resData || !resData.results) return;

        // Group by obstacle
        const resultsByObs = {};
        resData.results.forEach(r => {
            const rwyKey = `${arp.icao}_${r.runway}`;
            if (rwyVisible[rwyKey] === false) return;

            if (!resultsByObs[r.obstacle_id]) resultsByObs[r.obstacle_id] = [];
            resultsByObs[r.obstacle_id].push(r);
        });

        // Build flat array for rendering
        Object.entries(resultsByObs).forEach(([obsId, evalList]) => {
            evalList.forEach((ev, idx) => {
                tableRows.push({
                    isFirst: idx === 0,
                    rowSpan: evalList.length,
                    icao: arp.icao,
                    obsId: ev.obstacle_id,
                    obsName: ev.obstacle_name,
                    runway: ev.runway,
                    elevM: ev.elevation_m,
                    vTolM: ev.v_tolerance_m,
                    surfaceElevM: ev.surface_elev_m,
                    pdg: ev.pdg_percent,
                    result: ev.result,
                    margin: ev.surface_elev_m - (ev.elevation_m + ev.v_tolerance_m),
                });
            });
        });
    });

    // Mark the controlling obstacle (lowest clearance margin) per aerodrome+runway
    {
        const worstByRunway = new Map();
        tableRows.forEach(r => {
            const key = `${r.icao}_${r.runway}`;
            const cur = worstByRunway.get(key);
            if (!cur || r.margin < cur.margin) worstByRunway.set(key, r);
        });
        worstByRunway.forEach(r => { r.isControlling = true; });
    }

    const renderResultsContent = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px', height: '100%', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 'bold', color: T.white }}>Departure OIS Results</div>
                    {tableRows.length > 0 && (
                        <button
                            onClick={() => {
                                const headers = ["Obstacle ID", "Obstacle Name", "Elevation (m)", "V Tolerance (m)", "Aerodrome", "Runway", "Surface Elev (m)", "PDG (%)", "Result", "Controlling"];
                                const rows = tableRows.map(r => [
                                    r.obsId,
                                    r.obsName,
                                    r.elevM,
                                    r.vTolM,
                                    r.icao,
                                    r.runway,
                                    r.surfaceElevM,
                                    r.pdg > 0 ? r.pdg : '-',
                                    r.result,
                                    r.isControlling ? 'Yes' : ''
                                ]);
                                exportToCsv(headers, rows, `Departure_OIS_Assessment.csv`);
                            }}
                            style={{
                                padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                background: T.blue, color: T.white, fontSize: 11, fontWeight: 700,
                                display: 'flex', alignItems: 'center', gap: 4
                            }}
                        >
                            📥 Export CSV
                        </button>
                    )}
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, textAlign: 'left' }}>
                        <thead style={{ position: 'sticky', top: 0, background: T.bg0, zIndex: 1 }}>
                            <tr>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Obstacle</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Elevation (m)</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>V Tolerance (m)</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Aerodrome & Runway</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Surface Elev (m)</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>PDG (%)</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Result</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableRows.length > 0 ? tableRows.map((r, i) => (
                                <tr key={i} style={{ borderBottom: `1px solid ${T.border}33`, background: r.result === 'IMPACT' ? 'rgba(232, 64, 64, 0.08)' : 'transparent' }}>
                                    {r.isFirst && (
                                        <>
                                            <td rowSpan={r.rowSpan} style={{ padding: '8px 16px', borderRight: `1px solid ${T.border}33`, color: T.white }}>
                                                {r.obsName}
                                                <div style={{ fontSize: 10, color: T.muted }}>{r.obsId}</div>
                                            </td>
                                            <td rowSpan={r.rowSpan} style={{ padding: '8px 16px', borderRight: `1px solid ${T.border}33`, color: T.white }}>
                                                {r.elevM}
                                            </td>
                                            <td rowSpan={r.rowSpan} style={{ padding: '8px 16px', borderRight: `1px solid ${T.border}33`, color: T.white }}>
                                                {r.vTolM}
                                            </td>
                                        </>
                                    )}
                                    <td style={{ padding: '8px 16px', color: T.blueLt }}>
                                        {r.icao} RWY {r.runway} {r.isControlling && <span style={{ color: T.blue, fontSize: 9 }}>[Controlling]</span>}
                                    </td>
                                    <td style={{ padding: '8px 16px', color: T.white }}>{r.surfaceElevM}</td>
                                    <td style={{ padding: '8px 16px', color: T.white, fontWeight: 700 }}>{r.pdg > 0 ? r.pdg : '—'}</td>
                                    <td style={{ padding: '8px 16px' }}>
                                        {r.result === 'IMPACT' ? (
                                            <span style={{ color: T.danger, fontWeight: 700 }}>IMPACT</span>
                                        ) : (
                                            <span style={{ color: T.safe }}>No Impact</span>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="7" style={{ padding: '24px', textAlign: 'center', color: T.muted }}>
                                        No evaluation results to display. Select an aerodrome and ensure obstacle coverage.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const sidRows = activeSidProc?.results || [];
    const renderSidResultsContent = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px', height: '100%', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 'bold', color: T.white }}>
                        {activeSidProc ? `${activeSidProc.procedure_name} — PANS-OPS Clearance` : 'SID Departure Procedures'}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {activeSidProc && (
                            <>
                                <button
                                    onClick={() => handleSidScreenshot(activeSidProc.id)}
                                    title="Capture Screenshot of map pane"
                                    style={{
                                        padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                                        background: capturedSidPics[activeSidProc.id] ? 'rgba(46,201,139,0.15)' : 'rgba(255,255,255,0.05)',
                                        border: `1px solid ${capturedSidPics[activeSidProc.id] ? T.safe : T.border}`,
                                        color: capturedSidPics[activeSidProc.id] ? T.safe : T.white,
                                        display: 'flex', alignItems: 'center', gap: 4
                                    }}
                                >
                                    {capturedSidPics[activeSidProc.id] ? '✓ Captured' : '📷 Capture Screenshot'}
                                </button>
                                {capturedSidPics[activeSidProc.id] && (
                                    <button
                                        title="Clear Capture"
                                        onClick={() => handleClearSidScreenshot(activeSidProc.id)}
                                        style={{ padding: '4px', width: 24, background: 'rgba(255,255,255,0.07)', border: `1px solid ${T.border}`, borderRadius: 4, color: T.danger, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        ✕
                                    </button>
                                )}
                            </>
                        )}
                        {sidRows.length > 0 && (
                            <button
                                onClick={() => {
                                    const headers = ["#", "Obstacle", "Segment", "Area", "Distance (m)", "Prior Turn >15°", "MOCp (m)", "MOC Applied (m)", "y (m)", "Ws (m)", "Required PDG (%)", "Verdict", "Controlling"];
                                    const rows = sidRows.map((r, i) => [
                                        i + 1, r.obstacle_name, r.segment, r.area_type, r.distance_m, r.has_prior_turn ? 'Yes' : 'No',
                                        r.mocp_m, r.moc_applied_m, r.y_m ?? '-', r.ws_m ?? '-', r.percentage ?? '-', r.verdict, r.is_controlling ? 'Yes' : ''
                                    ]);
                                    exportToCsv(headers, rows, `${activeSidProc.procedure_name.replace(/\s+/g, '_')}_SID_Assessment.csv`);
                                }}
                                style={{ padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', background: T.blue, color: T.white, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}
                            >
                                📥 Export CSV
                            </button>
                        )}
                    </div>
                </div>
                {activeSidProc && (
                    <div style={{ fontSize: 11, color: T.muted, flexShrink: 0 }}>
                        Airport: <b style={{ color: T.white }}>{activeSidProc.airport_icao}</b> &nbsp;·&nbsp;
                        Runway: <b style={{ color: T.white }}>{activeSidProc.runway}</b> &nbsp;·&nbsp;
                        DER Elevation: <b style={{ color: T.white }}>{activeSidProc.der_elev_m} m</b>
                    </div>
                )}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, textAlign: 'left' }}>
                        <thead style={{ position: 'sticky', top: 0, background: T.bg0, zIndex: 1 }}>
                            <tr>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>#</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Obstacle</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Segment</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Area</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Distance (m)</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Prior Turn &gt;15°</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>MOCp (m)</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>MOC Applied (m)</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>y / Ws (m)</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Required PDG (%)</th>
                                <th style={{ padding: '8px 16px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Verdict</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sidRows.length > 0 ? sidRows.map((r, i) => (
                                <tr key={i} style={{ borderBottom: `1px solid ${T.border}33`, background: r.verdict === 'Penetrates' ? 'rgba(232, 64, 64, 0.08)' : 'transparent' }}>
                                    <td style={{ padding: '8px 16px', color: T.muted }}>{i + 1}</td>
                                    <td style={{ padding: '8px 16px', color: T.white }}>
                                        {r.obstacle_name}
                                        <div style={{ fontSize: 10, color: T.muted }}>Elev {r.obstacle_elev_m} m · VTol {r.v_tolerance_m} m · h_obs {r.h_obs_m} m</div>
                                    </td>
                                    <td style={{ padding: '8px 16px', color: T.white }}>{r.segment}<div style={{ fontSize: 10, color: T.muted }}>Seg {r.segment_index}</div></td>
                                    <td style={{ padding: '8px 16px', color: r.area_type === 'secondary' ? '#ffaa00' : '#0078ff', textTransform: 'capitalize' }}>{r.area_type}</td>
                                    <td style={{ padding: '8px 16px', color: T.white }}>{r.distance_m}</td>
                                    <td style={{ padding: '8px 16px', color: r.has_prior_turn ? '#ffaa00' : T.muted }}>{r.has_prior_turn ? 'Yes' : 'No'}</td>
                                    <td style={{ padding: '8px 16px', color: T.white }}>{r.mocp_m}</td>
                                    <td style={{ padding: '8px 16px', color: T.white }}>{r.moc_applied_m}</td>
                                    <td style={{ padding: '8px 16px', color: T.white }}>{r.area_type === 'secondary' ? `${r.y_m} / ${r.ws_m}` : '—'}</td>
                                    <td style={{ padding: '8px 16px', fontWeight: 700, color: T.white }}>{r.percentage ?? '—'}</td>
                                    <td style={{ padding: '8px 16px' }}>
                                        <span style={{
                                            color: r.verdict === 'Penetrates' ? T.danger : r.verdict === 'Close-in' ? '#ffaa00' : T.safe,
                                            fontWeight: 700,
                                        }}>
                                            {r.verdict}
                                        </span>
                                        {r.is_controlling && <span style={{ color: T.blue, fontSize: 9, marginLeft: 6 }}>[Controlling]</span>}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="10" style={{ padding: '24px', textAlign: 'center', color: T.muted }}>
                                        {sidLoading ? 'Loading SID departure procedures…' : sidError ? sidError :
                                            (activeSidProc ? 'No obstacles fall within this procedure\'s protection areas.' : 'No SID departure procedures within 30 NM of the obstacle hull.')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    async function handleExportAixm() {
        if (!project?.id) return;
        setExportingAixm(true);
        try {
            const resp = await fetch(`${API_BASE}/projects/${project.id}/export/aixm?surfaces=sid`);
            if (!resp.ok) {
                const body = await resp.json().catch(() => ({}));
                throw new Error(body.detail || `Export failed (${resp.status})`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const cd = resp.headers.get('content-disposition') || '';
            const nameMatch = cd.match(/filename="?([^";]+)"?/);
            link.download = nameMatch ? nameMatch[1] : `project_${project.id}_export_SID_AIXM51.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export AIXM:', err);
            alert(err.message || 'Failed to export AIXM. Please check your network connection and try again.');
        } finally {
            setExportingAixm(false);
        }
    }

    return (
        <div style={{ display: 'flex', height: '100%', flexDirection: 'column' }}>

            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                <div style={{ width: 280, background: T.bg1, backdropFilter: 'blur(var(--blur-md))', WebkitBackdropFilter: 'blur(var(--blur-md))', borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}` }}>
                        <button onClick={() => setResultsView('ois')} style={{ flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer', background: resultsView === 'ois' ? 'rgba(58,138,196,0.15)' : 'transparent', color: resultsView === 'ois' ? T.blue : T.muted, fontSize: 12, fontWeight: 700, borderBottom: resultsView === 'ois' ? `2px solid ${T.blue}` : '2px solid transparent' }}>
                            🛫 Surface OIS
                        </button>
                        <button onClick={() => setResultsView('sid')} style={{ flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer', background: resultsView === 'sid' ? 'rgba(58,138,196,0.15)' : 'transparent', color: resultsView === 'sid' ? T.blue : T.muted, fontSize: 12, fontWeight: 700, borderBottom: resultsView === 'sid' ? `2px solid ${T.blue}` : '2px solid transparent' }}>
                            ↗️ SID Procedures
                        </button>
                    </div>

                    <div style={{ padding: 16, borderBottom: `1px solid ${T.border}` }}>
                        <div style={{ fontSize: 11, color: T.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Target Obstacle</div>
                        <select value={selObstId || ''} onChange={(e) => setSelId(e.target.value)} style={{ width: '100%', background: T.bg0, border: `1px solid ${T.border}`, color: T.white, padding: '6px 8px', borderRadius: 4, fontSize: 13, outline: 'none' }}>
                            {obsWithCoords.map(o => <option key={o.id} value={o.id}>{o.name || 'Unnamed'}</option>)}
                        </select>
                        <button
                            onClick={handleExportAixm}
                            disabled={exportingAixm}
                            style={{ marginTop: 10, padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.blue}44`, cursor: exportingAixm ? 'default' : 'pointer', background: `${T.blue}11`, color: T.blue, fontSize: 10, fontWeight: 700, opacity: exportingAixm ? 0.6 : 1 }}
                            title="Export obstacles, aerodrome data, waypoints and SID departure procedures (StandardInstrumentDeparture) as AIXM 5.1"
                        >
                            {exportingAixm ? '⏳…' : '📤 AIXM'}
                        </button>
                    </div>

                    {resultsView === 'sid' ? (
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontSize: 11, color: T.muted }}>
                                    {sidLoading ? 'Loading…' : `${sidProcedures.length} procedure${sidProcedures.length === 1 ? '' : 's'} within 30 NM of obstacle hull`}
                                </div>
                                {sidProcedures.length > 0 && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: allSidVisible ? T.white : T.muted, cursor: 'pointer', userSelect: 'none' }}>
                                        <input
                                            type="checkbox"
                                            checked={allSidVisible}
                                            ref={el => { if (el) el.indeterminate = someSidVisible; }}
                                            onChange={e => toggleAllSid(e.target.checked)}
                                            style={{ accentColor: T.blue, cursor: 'pointer' }}
                                        />
                                        All
                                    </label>
                                )}
                            </div>
                            {sidError && <div style={{ padding: '0 16px 10px', fontSize: 11, color: T.danger }}>{sidError}</div>}
                            {sidProcedures.map(proc => {
                                const hitCount = (proc.results || []).length;
                                const isActive = proc.id === activeSidProcId;
                                return (
                                    <div key={proc.id} style={{ borderBottom: `1px solid ${T.border}33` }}>
                                        <div
                                            onClick={() => setActiveSidProcId(proc.id)}
                                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer', background: isActive ? 'rgba(58,138,196,0.1)' : 'transparent' }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={sidVisible[proc.id] !== false}
                                                onClick={e => e.stopPropagation()}
                                                onChange={() => setSidVisible(p => ({ ...p, [proc.id]: p[proc.id] === false }))}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: isActive ? T.blue : T.white }}>{proc.procedure_name}</div>
                                                <div style={{ fontSize: 10, color: T.muted }}>
                                                    RWY {proc.runway} · DER {proc.der_elev_m}m
                                                    {hitCount > 0 && <span style={{ color: '#ff9800', marginLeft: 6 }}>· {hitCount} obstacle hit{hitCount === 1 ? '' : 's'}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {!sidLoading && sidProcedures.length === 0 && !sidError && (
                                <div style={{ padding: 16, fontSize: 12, color: T.muted, textAlign: 'center' }}>No SID departure procedures within 30 NM.</div>
                            )}
                        </div>
                    ) : (
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {validArps.map(arp => {
                            const resData = depResults[arp.icao]?.data;
                            const err = depResults[arp.icao]?.error;
                            const isLoading = depResults[arp.icao]?.loading;
                            
                            // Distinct runways
                            const runways = [];
                            if (resData && resData.polygons && resData.polygons.features) {
                                resData.polygons.features.forEach(f => runways.push(f.properties.runway));
                            }

                            return (
                                <div key={arp.icao} style={{ borderBottom: `1px solid ${T.border}33` }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer', background: visible[arp.icao] ? 'rgba(58,138,196,0.05)' : 'transparent' }}>
                                        <input type="checkbox" checked={!!visible[arp.icao]} onChange={() => setVisible(p => ({ ...p, [arp.icao]: !p[arp.icao] }))} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: visible[arp.icao] ? T.blue : T.white }}>{arp.icao}</div>
                                            <div style={{ fontSize: 10, color: T.muted }}>{arp.name} · {arp.distance_km} km</div>
                                        </div>
                                    </label>
                                    
                                    <div style={{ display: 'flex', gap: 6, padding: '0 16px 10px', alignItems: 'flex-start' }}>
                                        <button onClick={() => handleLoadOis(arp.icao)} disabled={isLoading || !!resData} style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, background: resData ? 'transparent' : 'rgba(58,138,196,0.15)', border: `1px solid ${resData ? 'transparent' : T.blue}44`, borderRadius: 6, color: resData ? T.muted : T.blue, cursor: resData ? 'default' : 'pointer', flex: 1 }}>
                                            {isLoading ? 'Loading...' : (resData ? 'DATA LOADED' : '▶ LOAD OIS')}
                                        </button>
                                        <div style={{ display: 'flex', gap: 2 }}>
                                            <button onClick={() => handleScreenshot(arp.icao)} disabled={!resData} title="Capture Screenshot of map pane" style={{ padding: '6px 10px', background: capturedPics[arp.icao] ? 'rgba(46,201,139,0.15)' : 'rgba(255,255,255,0.05)', border: `1px solid ${capturedPics[arp.icao] ? T.safe : T.border}`, borderRadius: 6, color: capturedPics[arp.icao] ? T.safe : T.white, cursor: resData ? 'pointer' : 'not-allowed', fontSize: 10, fontWeight: 700 }}>
                                                {capturedPics[arp.icao] ? '✓ Captured' : 'Capture Screenshot'}
                                            </button>
                                            {capturedPics[arp.icao] && (
                                                <button title="Clear Capture" onClick={() => handleClearScreenshot(arp.icao)} style={{ padding: '6px', width: 24, background: 'rgba(255,255,255,0.07)', border: `1px solid ${T.border}`, borderRadius: 6, color: T.danger, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    ✕
                                                </button>
                                            )}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDownloadKmz(arp.icao, '2d'); }}
                                                    title="Download 2D KMZ (Flat Footprints)"
                                                    style={{
                                                        background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`,
                                                        borderRadius: 4, padding: '1px 4px', cursor: 'pointer', marginLeft: 2,
                                                        color: T.muted, fontSize: 9
                                                    }}
                                                >
                                                    KMZ (2D)
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDownloadKmz(arp.icao, '3d'); }}
                                                    title="Download 3D KMZ (True Altitudes)"
                                                    style={{
                                                        background: 'rgba(230,126,34,0.15)', border: `1px solid #E67E2244`,
                                                        borderRadius: 4, padding: '1px 4px', cursor: 'pointer', marginLeft: 2,
                                                        color: '#E67E22', fontSize: 9
                                                    }}
                                                >
                                                    KMZ (3D)
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setPreview3DIcao(arp.icao); }}
                                                    title="Preview the Departure OIS and project obstacles in 3D, right here"
                                                    style={{
                                                        background: 'rgba(46,204,113,0.15)', border: '1px solid #2ECC7144',
                                                        borderRadius: 4, padding: '1px 4px', cursor: 'pointer', marginLeft: 2,
                                                        color: '#2ECC71', fontSize: 9
                                                    }}
                                                >
                                                    🌐 3D
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {err && <div style={{ padding: '4px 16px 12px', fontSize: 11, color: T.danger }}>{err}</div>}
                                    
                                    {visible[arp.icao] && runways.length > 0 && (
                                        <div style={{ padding: '0px 16px 12px 36px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {runways.map(rwy => {
                                                const key = `${arp.icao}_${rwy}`;
                                                const checked = rwyVisible[key] !== false;
                                                return (
                                                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                                        <input type="checkbox" checked={checked} onChange={() => setRwyVisible(p => ({ ...p, [key]: !checked }))} />
                                                        <span style={{ fontSize: 12, color: checked ? T.white : T.muted }}>Departure RWY {rwy}</span>
                                                    </label>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    )}
                </div>

                <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', gap: 6, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 6, border: `1px solid ${T.border}`, backdropFilter: 'blur(8px)', alignItems: 'center' }}>
                        {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                            <button key={key} onClick={() => setMapMode(key)} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: mapMode === key ? T.blue : 'transparent', color: mapMode === key ? T.white : T.muted, fontSize: 11, fontWeight: mapMode === key ? 700 : 400 }}>{layer.label}</button>
                        ))}
                    </div>
                    <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportCsv}
                        penetratingCount={sidShieldRealPenetration.size}
                        onExportPenetrating={() => {
                            const byId = new Map();
                            [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
                             ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
                                byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
                            });
                            const entries = [];
                            sidShieldRealPenetration.forEach((penList, obsId) => {
                                const r = byId.get(obsId);
                                if (!r) return;
                                penList.forEach(v => entries.push({
                                    r,
                                    surfaceLabel: `SID / Departure OIS RWY ${v.runway}`,
                                    penetrationAmount: (-v.margin).toFixed(2),
                                }));
                            });
                            exportSurfacePenetratingCsv(entries, radiusKm);
                        }} />
                    {shieldOn && hasSidImpact && (
                        <div style={{ position: 'absolute', top: 226, right: 12, zIndex: 1000, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 8, border: '1px solid #FFB02055', backdropFilter: 'blur(8px)', maxWidth: 260, fontSize: 10, color: T.muted, lineHeight: 1.5 }}>
                            <div style={{ color: '#FFB020', fontWeight: 700, marginBottom: 2 }}>🛫 SID / Departure OIS (real check)</div>
                            {sidShieldLoading ? 'Checking real penetration…' : (
                                sidShieldVerdicts.size > 0
                                    ? <span><b style={{ color: '#FFB020' }}>{sidShieldVerdicts.size}</b> obstacle(s) confirmed to already penetrate the OIS as much or more than this project's worst case — not a new impact.</span>
                                    : 'No candidate already penetrates the OIS at least as much as this project — flat elevation comparison alone would be misleading here.'
                            )}
                        </div>
                    )}
                    <MapMeasureTool map={mapState} top={52} tableOffset={resultsCollapsed ? 40 : 300} />
                    <MapCoordinateViewer map={mapState} />
                    <div ref={mapRef} style={{ flex: 1 }} />

                    <div style={{ height: resultsCollapsed ? 38 : resultsHeight, background: T.bg1, borderTop: `2px solid ${T.border}`, display: 'flex', flexDirection: 'column', transition: 'height 0.2s ease', overflow: 'hidden' }}>
                        {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                        <div style={{ padding: '8px 20px', background: T.bg0, borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: T.white, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>{resultsView === 'sid' ? '↗️' : '🛫'}</span> {resultsView === 'sid' ? 'SID Departure Procedure Report' : 'Departure OIS Report'}
                            </div>
                            <div style={{ display: 'flex', gap: 12 }}>
                                {!resultsPoppedOut && (
                                    <button
                                        onClick={() => {
                                            setResultsPoppedOut(true);
                                            setResultsCollapsed(true);
                                        }}
                                        style={{
                                            background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                            display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600
                                        }}
                                        title="Pop out results to a movable window"
                                    >
                                        🗖 Pop Out
                                    </button>
                                )}
                                <button
                                    onClick={() => setResultsCollapsed(!resultsCollapsed)}
                                    style={{
                                        background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                        fontWeight: 600
                                    }}
                                >
                                    {resultsCollapsed ? '▲ Expand' : '▼ Collapse'}
                                </button>
                            </div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {resultsPoppedOut ? (
                                <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                    Results are popped out. 
                                    <button 
                                        onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }} 
                                        style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                    >
                                        Bring back
                                    </button>
                                </div>
                            ) : (resultsView === 'sid' ? renderSidResultsContent() : renderResultsContent())}
                        </div>
                    </div>
                </div>
            </div>

            {resultsPoppedOut && (
                <DraggableModal
                    title={`${project?.name || ''} - ${resultsView === 'sid' ? 'SID Departure Procedure Results' : 'Departure OIS Results'}`}
                    onClose={() => setResultsPoppedOut(false)}
                    width={900}
                    height={500}
                >
                    {resultsView === 'sid' ? renderSidResultsContent() : renderResultsContent()}
                </DraggableModal>
            )}

            {preview3DIcao && (
                <Cesium3DPreviewModal
                    title={`${preview3DIcao} — Departure OIS 3D Preview`}
                    kmlUrl={`${API_BASE}/departure-ois/${preview3DIcao}/kml?mode=3d${project?.id ? `&project_id=${project.id}` : ''}`}
                    onClose={() => setPreview3DIcao(null)}
                />
            )}
        </div>
    );
}
